import { useState, useCallback, useEffect } from 'react'
import { prodexer } from '../api'
import type { Block, Transaction, Transfer } from '../types'
import {
  useQuery,
  SearchInput,
  Spinner,
  ErrorBanner,
  SectionHeader,
  DataTable,
  LinkToCopy,
  walletUrl,
  blockUrl,
  txUrl,
  AmountDisplay,
  formatNumber,
  truncate,
  formatTime,
  renderJsonData,
  ClickToCopy,
  txHashToDisplayHex,
} from '../components'

export function BlockLookup({ initialQuery }: { initialQuery?: string }) {
  const blockQ = useQuery<Block>()
  const txsQ = useQuery<Transaction>()
  const transfersQ = useQuery<Transfer>()

  const [hasSearched, setHasSearched] = useState(false)
  const [blockHeight, setBlockHeight] = useState('')

  const search = useCallback(async (heightStr: string) => {
    const height = parseInt(heightStr, 10)
    if (isNaN(height)) return
    setBlockHeight(heightStr)
    setHasSearched(true)

    await Promise.all([
      blockQ.execute(() =>
        prodexer.query<Block>('blocks', {
          filters: [{ column: 'height', op: 'eq', value: String(height) }],
          limit: 1,
        })
      ),
      txsQ.execute(() =>
        prodexer.query<Transaction>('transactions', {
          filters: [{ column: 'block_height', op: 'eq', value: String(height) }],
          // No order: idx is (block_height, block_index) but order=block_index
          // requires block_index to be after block_height in the index — it IS,
          // but canUseIndex checks that all filter cols are a prefix of the index.
          // filter=[block_height], order=[block_index]: block_index is at pos 1,
          // block_height at pos 0 is in filter_set → should work.
          order: [{ column: 'block_index', desc: false }],
          limit: 100,
        })
      ),
      transfersQ.execute(() =>
        prodexer.query<Transfer>('transfers', {
          filters: [{ column: 'block_height', op: 'eq', value: String(height) }],
          // No order: the transfers table index is (sender, block_height) or
          // (recipient, block_height) — ordering by block_index after filtering
          // on block_height fails canUseIndex. Transfers within a block are
          // few, so default ordering is sufficient.
          limit: 100,
        })
      ),
    ])
  }, [blockQ, txsQ, transfersQ])

  // Auto-search on initialQuery (URL deep-link).
  // App.tsx uses key={block-${initialQuery}} to force remount, but we also
  // depend on initialQuery for safety (cubic P2).
  useEffect(() => {
    if (initialQuery) {
      search(initialQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery])

  const block = blockQ.data[0]
  const loading = blockQ.loading || txsQ.loading || transfersQ.loading
  // Only block query is critical — tx/transfers failures show inline
  const error = blockQ.error
  const txsError = txsQ.error
  const transfersError = transfersQ.error

  return (
    <div>
      <SectionHeader
        title="Block Lookup"
        subtitle="Inspect a block's metadata, transactions, and token transfers."
      />

      <SearchInput
        placeholder="Block height (e.g. 6604916)"
        onSubmit={search}
        loading={loading}
        buttonText="Lookup Block"
        currentValue={blockHeight}
      />

      {loading && (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {error && !loading && (
        <div className="mt-4">
          <ErrorBanner error={error} />
        </div>
      )}

      {!loading && hasSearched && !error && (
        <div className="mt-6 space-y-6">
          {block ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Height</div>
                  <div className="mt-1 text-xl font-semibold font-mono text-accent-text">
                    <LinkToCopy text={String(block.height)} href={blockUrl(block.height)} display={<span>{formatNumber(block.height)}</span>} />
                  </div>
                </div>
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Chain ID</div>
                  <div className="mt-1 text-sm font-mono text-text-secondary">{block.chain_id}</div>
                </div>
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Consensus Time</div>
                  <div className="mt-1 text-sm font-mono text-text-secondary">
                    {formatTime(block.consensus_time)}
                  </div>
                </div>
                <div className="rounded-lg border border-border-default bg-surface p-4">
                  <div className="text-xs uppercase tracking-wider text-text-faint">Status</div>
                  <div className="mt-1 text-sm font-mono">
                    <span className={block.done ? 'text-success-text' : 'text-amber-text'}>
                      {block.done ? '✓ Processed' : '⏳ Pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Block details: hash, indexed-at, error */}
              <div className="rounded-lg border border-border-default bg-surface p-4">
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Block Hash</dt>
                    <dd className="text-sm font-mono text-text-secondary">
                      <ClickToCopy text={txHashToDisplayHex(block.block_hash)} display={<span className="break-all">{truncate(txHashToDisplayHex(block.block_hash), 16, 8)}</span>} className="hover:text-accent-text" />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5">
                    <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Indexed At</dt>
                    <dd className="text-sm font-mono text-text-secondary">{formatTime(block.created_at)}</dd>
                  </div>
                  {block.err && (
                    <div className="flex items-baseline justify-between gap-2 border-b border-border-faint pb-1.5 md:col-span-2">
                      <dt className="text-xs uppercase tracking-wider text-text-faint whitespace-nowrap">Error</dt>
                      <dd className="text-sm font-mono text-danger-text break-all text-right">{block.err}</dd>
                    </div>
                  )}
                </dl>

                {/* Raw block JSON */}
                <div className="mt-3">
                  <details>
                    <summary className="cursor-pointer text-xs uppercase tracking-wider text-text-faint hover:text-text-muted">
                      Raw Block JSON
                    </summary>
                    <pre className="mt-2 overflow-x-auto rounded bg-code-bg p-3 text-xs font-mono text-text-muted max-h-64">
                      {renderJsonData(block)}
                    </pre>
                  </details>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-text-faint italic">Block not found.</div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-text-secondary mb-2">
              Transactions ({txsQ.data?.length ?? 0})
            </h3>
            {txsError ? (
              <ErrorBanner error={txsError} />
            ) : (
            <DataTable
              rows={txsQ.data ?? []}
              emptyMessage="No transactions in this block"
              expandableKey="tx_hash"
              renderExpansion={(r) => (
                <div className="space-y-3 py-2 px-1">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <div className="text-text-faint uppercase tracking-wider">TX Hash</div>
                      <div className="mt-0.5 font-mono text-text-secondary">
                        <LinkToCopy
                          text={txHashToDisplayHex(r.tx_hash)}
                          href={txUrl(txHashToDisplayHex(r.tx_hash))}
                          display={<span className="break-all">{truncate(txHashToDisplayHex(r.tx_hash), 16, 8)}</span>}
                          className="text-text-secondary hover:text-accent-text"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="text-text-faint uppercase tracking-wider">Status</div>
                      <div className={`mt-0.5 font-mono ${r.status_code === 0 ? 'text-success-text' : 'text-danger-text'}`}>
                        {r.status_code === 0 ? '✓ Success' : `✗ Code ${r.status_code}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-text-faint uppercase tracking-wider">Gas Wanted</div>
                      <div className="mt-0.5 font-mono text-text-secondary">{formatNumber(r.gas_wanted)}</div>
                    </div>
                    <div>
                      <div className="text-text-faint uppercase tracking-wider">Gas Used</div>
                      <div className="mt-0.5 font-mono text-text-secondary">
                        {formatNumber(r.gas_used)}
                        {r.gas_wanted > 0 && (
                          <span className="text-text-faint ml-1">
                            ({((r.gas_used / r.gas_wanted) * 100).toFixed(1)}%)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Message types (full) */}
                  <div>
                    <span className="text-xs uppercase tracking-wider text-text-faint mr-2">Messages:</span>
                    <div className="inline-flex flex-wrap gap-1">
                      {r.message_types.map((mt, i) => (
                        <span
                          key={i}
                          className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs font-mono text-accent-text"
                        >
                          {mt}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Codespace */}
                  {r.codespace && (
                    <div className="text-xs">
                      <span className="text-text-faint mr-1">Codespace:</span>
                      <span className="font-mono text-text-muted">{r.codespace}</span>
                    </div>
                  )}

                  {/* Memo */}
                  {r.memo && (
                    <div className="text-xs">
                      <span className="text-text-faint mr-1">Memo:</span>
                      <span className="font-mono text-text-muted">{r.memo}</span>
                    </div>
                  )}

                  {/* Raw log */}
                  {r.raw_log && (
                    <div className="text-xs">
                      <details>
                        <summary className="cursor-pointer text-text-faint hover:text-text-muted">
                          Raw Log
                        </summary>
                        <pre className="mt-1 overflow-x-auto rounded bg-code-bg p-2 text-xs font-mono text-text-muted max-h-48">
                          {r.raw_log}
                        </pre>
                      </details>
                    </div>
                  )}

                  {/* Raw JSON */}
                  <div className="text-xs">
                    <details>
                      <summary className="cursor-pointer text-text-faint hover:text-text-muted">
                        JSON Data
                      </summary>
                      <pre className="mt-1 overflow-x-auto rounded bg-code-bg p-2 text-xs font-mono text-text-muted max-h-48">
                        {renderJsonData(r.json_data)}
                      </pre>
                    </details>
                  </div>
                </div>
              )}
              columns={[
                {
                  key: 'block_index',
                  label: 'Idx',
                },
                {
                  key: 'sender_address',
                  label: 'Sender',
                  render: (r) => (
                    <LinkToCopy text={r.sender_address} href={walletUrl(r.sender_address)} display={<span className="text-accent-text">{truncate(r.sender_address)}</span>} className="hover:text-accent-text" />
                  ),
                },
                {
                  key: 'message_types',
                  label: 'Messages',
                  render: (r) => (
                    <div className="flex flex-wrap gap-1">
                      {r.message_types.map((mt, i) => (
                        <span
                          key={i}
                          className="rounded bg-bg-tertiary px-1.5 py-0.5 text-xs text-accent-text"
                        >
                          {mt.split('.').pop()}
                        </span>
                      ))}
                    </div>
                  ),
                },
                {
                  key: 'status_code',
                  label: 'Status',
                  render: (r) => (
                    <span className={r.status_code === 0 ? 'text-success-text' : 'text-danger-text'}>
                      {r.status_code === 0 ? '✓' : `✗ ${r.status_code}`}
                    </span>
                  ),
                },
                {
                  key: 'gas_used',
                  label: 'Gas',
                  render: (r) => formatNumber(r.gas_used),
                },
              ]}
            />
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-text-secondary mb-2">
              Transfers ({transfersQ.data?.length ?? 0})
            </h3>
            {transfersError ? (
              <ErrorBanner error={transfersError} />
            ) : (
            <DataTable
              rows={transfersQ.data ?? []}
              emptyMessage="No transfers in this block"
              columns={[
                { key: 'block_index', label: 'Idx' },
                {
                  key: 'sender',
                  label: 'Sender',
                  render: (r) => (
                    <LinkToCopy text={r.sender} href={walletUrl(r.sender)} display={<span className="text-accent-text">{truncate(r.sender)}</span>} className="hover:text-accent-text" />
                  ),
                },
                {
                  key: 'recipient',
                  label: 'Recipient',
                  render: (r) => (
                    <LinkToCopy text={r.recipient} href={walletUrl(r.recipient)} display={<span className="text-success-text">{truncate(r.recipient)}</span>} className="hover:text-success-text" />
                  ),
                },
                {
                  key: 'amount',
                  label: 'Amount (ALLO)',
                  render: (r) => (
                    <AmountDisplay value={r.amount} className="text-amber-text" />
                  ),
                },
                { key: 'denom', label: 'Denom' },
              ]}
            />
            )}
          </div>
        </div>
      )}

      {!hasSearched && (
        <div className="mt-8 text-center text-sm text-text-faint">
          Enter a block height to see its metadata, transactions, and transfers.
        </div>
      )}
    </div>
  )
}
